import { addLogDebug, addLogError, addLogWarn } from "./logger"
import { STORAGE_KEYS } from "@shared/constants/storageKeys"

// Key to track reload attempts to prevent infinite reload loops
const RELOAD_COOLDOWN_MS = 10000 // 10 seconds between reload attempts

export function isChunkLoadError(error: unknown): boolean {
  if (!error) return false

  // Error keeps its own "Name: message" rendering; anything else that is not
  // already text has no useful string form for the pattern checks below.
  const errorString = error instanceof Error ? error.toString() : typeof error === "string" ? error : ""
  const errorMessage = error instanceof Error ? error.message : ""

  // Detect various chunk load error patterns
  return (
    errorString.includes("Failed to fetch dynamically imported module") ||
    errorString.includes("Loading chunk") ||
    errorString.includes("Loading CSS chunk") ||
    errorMessage.includes("Failed to fetch dynamically imported module") ||
    errorMessage.includes("error loading dynamically imported module") ||
    // Vite specific errors
    errorString.includes("Unable to preload CSS") ||
    errorString.includes("Failed to load module script") ||
    // Safari/WebKit phrasing of a failed dynamic import. This is the single
    // string behind the "Importing a module script failed" issue that
    // dominates Sentry (iOS/Safari users landing on stale lazy-chunks right
    // after a deploy). It was named in the beforeSend comment as "covered"
    // but never actually added here, so those users got no auto-reload and
    // every occurrence leaked into Sentry.
    errorString.includes("Importing a module script failed")
  )
}

function shouldReloadForChunkError(): boolean {
  const lastReload = localStorage.getItem(STORAGE_KEYS.CHUNK_ERROR_RELOAD)
  if (!lastReload) return true

  const timeSinceLastReload = Date.now() - parseInt(lastReload, 10)
  return timeSinceLastReload > RELOAD_COOLDOWN_MS
}

export function reloadForChunkError() {
  if (!shouldReloadForChunkError()) {
    addLogWarn("Chunk load error detected but skipping reload (cooldown active)")
    return
  }

  addLogDebug("Chunk load error detected, reloading page...")
  localStorage.setItem(STORAGE_KEYS.CHUNK_ERROR_RELOAD, String(Date.now()))

  // Clear service worker cache and reload
  if (typeof caches !== "undefined") {
    caches
      .keys()
      .then((names) => {
        names.forEach((name) => {
          if (name.includes("static-assets") || name.includes("workbox")) {
            caches.delete(name).catch((error) => addLogError(`Failed to delete cache ${name}`, error))
          }
        })
      })
      .catch((error) => addLogError("Failed to list caches", error))
      .finally(() => {
        location.reload()
      })
  } else {
    location.reload()
  }
}

export function watchErrorGlobal() {
  window.onerror = function (message, source, lineno, colno, error) {
    addLogDebug("Global error:", message, source, lineno, colno, error)

    // Check for chunk load errors
    if (isChunkLoadError(error) || isChunkLoadError(message)) {
      reloadForChunkError()
    }
  }

  window.onunhandledrejection = function (event) {
    // console only: Sentry's own globalHandlers integration already captures this rejection with the original
    // reason, which is what index.tsx's beforeSend filters look at (a null reason is dropped there). Forwarding
    // it again through addLogError would send a prefixed string that slips past those filters.
    addLogWarn("Unhandled promise rejection:", event.reason)

    // Check for chunk load errors in promise rejections
    if (isChunkLoadError(event.reason)) {
      reloadForChunkError()
    }
  }
}
